/**
 * MODULE: _shared/frontend-shell/src/features/crm/context/CrmContext.tsx
 * PURPOSE: CRM UI state — `CrmContextState` via useReducer plus modals, contact and account drawer ids, DrilldownConfig bridge (hub filters live in the URL where pages use **`useCrmFilters`**)
 * DEPENDENCIES: react; @shared/lib/frontend/api/contactApi ManagedContact; ../types/crm.types; ../types/task.types; ../types/activity.types
 * PUBLIC_API: CrmProvider, useCrm, CrmContextValue; test exports **`crmReducer`**, **`initialCrmState`**
 * SIDE_EFFECTS: React reducer and local state updates
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 1041/1200
 * RECENT: 2026-05-11 Task 11 #92 — Langflow UI moves to app-shell **`AIAssistantDock`**; **`openCrmAssistant`** delegates to **`ShellAssistantProvider`** with Vitest fallback state — root **`CHANGELOG` [2.2.944]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.893] **`crmAiAssistantEmbedSlotEl`** + **`registerCrmAiAssistantEmbedSlot`** — superseded by bottom dock (**`CHANGELOG` [2.2.893]`**)
 * RECENT_PRIOR: 2026-05-08 [2.2.878] Docs sync — **`settleDestructiveDialog`** invokes resolver before **`setDestructiveDialog(null)`** so nested **`confirmDestructive`** awaits complete; root **`CHANGELOG` [2.2.878]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.876] CRM persistence UX compliance: persisted CRM create, edit, task, activity, deal, account, related-entity, and pipeline run-history flows; stale demo copy removed — root **`CHANGELOG` [2.2.876]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.875] **`refetchAllCrmLists`** **`useCallback`** deps are the five **`refetch`** functions only — avoids CRM list refetch loop after **`requestDataRefresh`** (whole hook return objects change every render).
 * RECENT_PRIOR: 2026-05-08 [2.2.873] **`addActivity`** **`await`**s **`useCrmActivities`** persist so **`logCall`** and post-call Save finish before UI continues — root **`CHANGELOG` [2.2.873]`**
 * RECENT_PRIOR: 2026-05-03 [2.2.791] **`refetchAllCrmLists`** hook dependency list; **`openDrilldown`** **`useCallback`** (no inner cast wrapper) — CHANGELOG [2.2.791]
 * RECENT_PRIOR: 2026-05-01 [2.2.762] **`crmContacts`**, **`emailComposeOpen`**, **`openEmailComposeModal`**, **`closeEmailComposeModal`** — CHANGELOG [2.2.762]
 * RECENT_PRIOR: 2026-04-30 [2.2.756] **`commandPaletteOpen`**, **`OPEN_COMMAND_PALETTE`**, **`closeCommandPalette`** — CHANGELOG [2.2.756]
 * RECENT_PRIOR: 2026-04-30 [2.2.754] **`crmAssistant`**, **`openCrmAssistant`**, **`closeCrmAssistant`**, **`initialCrmAssistantState`** — CHANGELOG [2.2.754]
 * RECENT_PRIOR: 2026-04-30 [2.2.750] **`contactListPatches`**, **`mergeContactListPatches`**, **`MERGE_CONTACT_LIST_PATCHES`** — CHANGELOG [2.2.750]. [2.2.748] URL-backed filters — CHANGELOG [2.2.748]
 * RECENT_PRIOR: 2026-04-30 [2.2.744] **`REQUEST_DATA_REFRESH`**, **`refetchAllCrmLists`**, **`requestDataRefresh`**, bootstrap errors; documentation compliance pass — CHANGELOG [2.2.744]
 * RECENT_PRIOR: 2026-04-30 [2.2.738] **`deletedActivityIds`**, **`removeActivity`**, filtered **`crmActivities`**, exported **`crmReducer`** — CHANGELOG [2.2.738]
 * RECENT_PRIOR: 2026-04-30 [2.2.735] Docs sync — CHANGELOG [2.2.735]
 * RECENT_PRIOR: 2026-04-30 [2.2.734] **`pipelineGenerationModalOpen`**, **`openPipelineModal`**, **`closePipelineModal`** — CHANGELOG [2.2.734]
 * RECENT_PRIOR: 2026-04-30 [2.2.732] **`crmActivities`**, **`crmOpportunities`**, derived **`opportunities`**, optimistic mutators — CHANGELOG [2.2.732]
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  ShellAssistantReactContext,
  sessionToCrmAssistantShape,
} from '../../../layout/shellAssistant';
import type { Account } from '@shared/lib/frontend/api/accountApi';
import type { ManagedContact } from '@shared/lib/frontend/api/contactApi';
import { DestructiveConfirmDialog } from '../components/shared/DestructiveConfirmDialog';
import { useCrmAccounts } from '../hooks/useCrmAccounts';
import { useCrmActivities } from '../hooks/useCrmActivities';
import { useCrmContacts } from '../hooks/useCrmContacts';
import { CRM_NEW_OPPORTUNITY_SENTINEL, useCrmDeals } from '../hooks/useCrmDeals';
import { useCrmTasks } from '../hooks/useCrmTasks';
import type { Activity } from '../types/activity.types';
import type { Opportunity } from '../types/opportunity.types';
import { useTenant } from '@core/tenant';
import { useTenantDefaultCurrency } from '@shared/lib/frontend/hooks/useTenantDefaultCurrency';
import { localeForCurrency } from '@shared/lib/frontend/utils/formatCurrency';
import type { Task } from '../types/task.types';
import type {
  ActivityModalOpenPrefill,
  CrmAction,
  CrmAiAssistantPageContext,
  CrmAssistantState,
  CrmContextState,
  CrmDestructiveConfirmOptions,
  CrmKpis,
  CrmScheduleModalPrefill,
  CrmTab,
  DrilldownConfig,
} from '../types/crm.types';

export type {
  ActivityModalOpenPrefill,
  CrmAction,
  CrmAiAssistantPageContext,
  CrmAssistantState,
  CrmDestructiveConfirmOptions,
  CrmScheduleModalPrefill,
} from '../types/crm.types';

/** Internal keys so `Record<string, unknown>` drilldown presets round-trip to `DrilldownConfig`. */
const DRILL_ENTITY = '__entityType';
const DRILL_SUBTITLE = '__subtitle';

/**
 * Flowbite `Modal` marks outside layers `aria-hidden` (Floating UI inert). If focus stays on a
 * drawer control when that runs, the browser warns. Blur before opening CRM overlays above drawers.
 */
function blurActiveElementBeforeCrmOverlay(): void {
  if (typeof document === 'undefined') return;
  const el = document.activeElement;
  if (el instanceof HTMLElement) el.blur();
}

/** Same string as `CRM_NEW_OPPORTUNITY_ID` in `hooks/useOpportunity.ts`. */

export const initialCrmAssistantState: CrmAssistantState = {
  open: false,
  prefillContext: '',
  suggestedPrompts: [],
  prefillNonce: 0,
  pageContext: null,
};

export const initialCrmState: CrmContextState = {
  dataRefreshNonce: 0,
  activeTab: 'home',
  openDrawerId: null,
  activeContactId: null,
  activeAccountId: null,
  openDrilldown: null,
  kpis: null,
  deletedOpportunityIds: [],
  deletedContactIds: [],
  deletedAccountIds: [],
  deletedActivityIds: [],
  contactListPatches: {},
  commandPaletteOpen: false,
};

type CrmReducerAction =
  | CrmAction
  | { type: 'SET_ACTIVE_TAB'; tab: string }
  | { type: 'OPEN_DRAWER'; opportunityId: string }
  | { type: 'CLOSE_DRAWER' }
  | { type: 'OPEN_CONTACT_DRAWER'; contactId: string }
  | { type: 'CLOSE_CONTACT_DRAWER' }
  | { type: 'OPEN_ACCOUNT_DRAWER'; accountId: string }
  | { type: 'CLOSE_ACCOUNT_DRAWER' }
  | { type: 'OPEN_DRILLDOWN'; title: string; filterPreset: Record<string, unknown> }
  | { type: 'CLOSE_DRILLDOWN' }
  | { type: 'SET_KPIS'; kpis: CrmKpis }
  | { type: 'REMOVE_OPPORTUNITY'; id: string }
  | { type: 'REMOVE_CONTACT'; id: string }
  | { type: 'REMOVE_ACCOUNT'; id: string }
  | { type: 'RESTORE_OPPORTUNITY'; id: string }
  | { type: 'RESTORE_CONTACT'; id: string }
  | { type: 'RESTORE_ACCOUNT'; id: string }
  | { type: 'REQUEST_DATA_REFRESH' }
  | {
      type: 'MERGE_CONTACT_LIST_PATCHES';
      patches: Record<string, { ownerId?: string; tags?: string[] } | null>;
    }
  | { type: 'OPEN_COMMAND_PALETTE' }
  | { type: 'CLOSE_COMMAND_PALETTE' };

export function crmReducer(state: CrmContextState, action: CrmReducerAction): CrmContextState {
  switch (action.type) {
    case 'OPEN_COMMAND_PALETTE':
      return { ...state, commandPaletteOpen: true };
    case 'CLOSE_COMMAND_PALETTE':
      return { ...state, commandPaletteOpen: false };
    case 'REQUEST_DATA_REFRESH':
      return { ...state, dataRefreshNonce: state.dataRefreshNonce + 1 };
    case 'MERGE_CONTACT_LIST_PATCHES': {
      const next = { ...state.contactListPatches };
      for (const [id, patch] of Object.entries(action.patches)) {
        if (patch === null) {
          delete next[id];
          continue;
        }
        const cur = next[id] ? { ...next[id] } : {};
        if (patch.ownerId !== undefined) cur.ownerId = patch.ownerId;
        if (patch.tags !== undefined) cur.tags = [...patch.tags];
        next[id] = cur;
      }
      return { ...state, contactListPatches: next };
    }
    case 'REMOVE_ACTIVITY': {
      if (state.deletedActivityIds.includes(action.id)) return state;
      return {
        ...state,
        deletedActivityIds: [...state.deletedActivityIds, action.id],
      };
    }
    case 'RESTORE_ACTIVITY':
      return {
        ...state,
        deletedActivityIds: state.deletedActivityIds.filter((x) => x !== action.id),
      };
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.tab };
    case 'OPEN_DRAWER':
      return { ...state, openDrawerId: action.opportunityId };
    case 'CLOSE_DRAWER':
      return { ...state, openDrawerId: null };
    case 'OPEN_CONTACT_DRAWER':
      return { ...state, activeContactId: action.contactId };
    case 'CLOSE_CONTACT_DRAWER':
      return { ...state, activeContactId: null };
    case 'OPEN_ACCOUNT_DRAWER':
      return { ...state, activeAccountId: action.accountId };
    case 'CLOSE_ACCOUNT_DRAWER':
      return { ...state, activeAccountId: null };
    case 'OPEN_DRILLDOWN':
      return {
        ...state,
        openDrilldown: { title: action.title, filterPreset: action.filterPreset },
      };
    case 'CLOSE_DRILLDOWN':
      return { ...state, openDrilldown: null };
    case 'SET_KPIS':
      return { ...state, kpis: action.kpis };
    case 'REMOVE_OPPORTUNITY': {
      if (state.deletedOpportunityIds.includes(action.id)) return state;
      return {
        ...state,
        deletedOpportunityIds: [...state.deletedOpportunityIds, action.id],
        openDrawerId: state.openDrawerId === action.id ? null : state.openDrawerId,
      };
    }
    case 'REMOVE_CONTACT': {
      if (state.deletedContactIds.includes(action.id)) return state;
      return {
        ...state,
        deletedContactIds: [...state.deletedContactIds, action.id],
        activeContactId: state.activeContactId === action.id ? null : state.activeContactId,
      };
    }
    case 'REMOVE_ACCOUNT': {
      if (state.deletedAccountIds.includes(action.id)) return state;
      return {
        ...state,
        deletedAccountIds: [...state.deletedAccountIds, action.id],
        activeAccountId: state.activeAccountId === action.id ? null : state.activeAccountId,
      };
    }
    case 'RESTORE_OPPORTUNITY':
      return {
        ...state,
        deletedOpportunityIds: state.deletedOpportunityIds.filter((x) => x !== action.id),
      };
    case 'RESTORE_CONTACT':
      return {
        ...state,
        deletedContactIds: state.deletedContactIds.filter((x) => x !== action.id),
      };
    case 'RESTORE_ACCOUNT':
      return {
        ...state,
        deletedAccountIds: state.deletedAccountIds.filter((x) => x !== action.id),
      };
    default:
      return state;
  }
}

function drilldownConfigFromShell(d: NonNullable<CrmContextState['openDrilldown']>): DrilldownConfig {
  const preset = { ...d.filterPreset };
  const rawEt = preset[DRILL_ENTITY];
  delete preset[DRILL_ENTITY];
  const rawSub = preset[DRILL_SUBTITLE];
  delete preset[DRILL_SUBTITLE];
  const entityRaw = typeof rawEt === 'string' ? rawEt : 'deals';
  const entityType =
    entityRaw === 'contacts' || entityRaw === 'accounts' || entityRaw === 'pipeline' || entityRaw === 'deals'
      ? entityRaw
      : 'deals';
  const filterPreset = Object.fromEntries(
    Object.entries(preset).map(([k, v]) => [k, v == null ? '' : String(v)])
  ) as Record<string, string>;
  return {
    title: d.title,
    subtitle: rawSub == null || String(rawSub).trim() === '' ? undefined : String(rawSub),
    filterPreset,
    entityType,
  };
}

export interface CrmContextValue {
  /** Instruction 3 — reducer snapshot (read-only) */
  crm: CrmContextState;

  /** Tenant display settings for CRM currency (future: wire from tenant profile API). */
  tenantCurrency: string;
  tenantLocale: string;

  activeTab: CrmTab;
  setActiveTab: (tab: CrmTab) => void;

  opportunityDrawerOpen: boolean;
  openOpportunityDrawer: (opportunityId: string) => void;
  closeOpportunityDrawer: () => void;
  activeOpportunityId: string | null;

  contactDrawerOpen: boolean;
  activeContactId: string | null;
  createContactPrefill: Record<string, string> | null;
  openContactDrawer: (contactId: string, prefill?: Record<string, string>) => void;
  closeContactDrawer: () => void;

  /**
   * Live contact-drawer aside DOM node (registered by ContactDrawer). Flowbite Modal `root` uses
   * this so Floating UI marks inert siblings inside the drawer, not the drawer root while it holds focus.
   */
  contactDrawerPortalAsideEl: HTMLElement | null;
  registerContactDrawerFlowbitePortalAside: (el: HTMLElement | null) => void;

  /** Outer `fixed` contact-drawer root (no `transform`) — `createPortal` target for call prep or active overlay when scoped to the open contact. */
  contactDrawerOverlayHostEl: HTMLElement | null;
  registerContactDrawerOverlayHost: (el: HTMLElement | null) => void;
  /** Live host element; use during render or layout so consumers are not stuck with a stale snapshot when CrmProvider has not re-rendered. */
  readContactDrawerOverlayHost: () => HTMLElement | null;
  /** Bumps when the overlay host registers — use in `useLayoutEffect` deps so call UI can attach after the same commit as the drawer shell. */
  contactDrawerOverlayHostGen: number;

  /** Legacy no-op — drawer embed removed; Langflow lives in app-shell **`AIAssistantDock`** (#92). */
  crmAiAssistantEmbedSlotEl: HTMLElement | null;
  registerCrmAiAssistantEmbedSlot: (el: HTMLElement | null) => void;

  accountDrawerOpen: boolean;
  activeAccountId: string | null;
  openAccountDrawer: (accountId: string) => void;
  closeAccountDrawer: () => void;
  createAccountModalOpen: boolean;
  createAccountPrefill: Record<string, string> | null;
  openCreateAccountModal: (prefill?: Record<string, string>) => void;
  closeCreateAccountModal: () => void;

  drilldownOpen: boolean;
  openDrilldown: ((config: DrilldownConfig) => void) & ((title: string, filterPreset: Record<string, unknown>) => void);
  closeDrilldown: () => void;
  activeDrilldown: DrilldownConfig | null;

  scheduleModalOpen: boolean;
  scheduleModalPrefill: CrmScheduleModalPrefill | null;
  scheduleModalMode: 'task' | 'activity';
  /** When set, ScheduleModal opens in edit mode for this task (Instruction 9). */
  scheduleModalEditingTask: Task | null;
  /** True when opening for update (explicit `editingTask` or `editMode` + resolved task). */
  scheduleModalEditMode: boolean;
  openScheduleModal: (
    prefill?: CrmScheduleModalPrefill,
    options?: { mode?: 'task' | 'activity'; editingTask?: Task; editMode?: boolean }
  ) => void;
  closeScheduleModal: () => void;

  /** Contacts list from data-management (used for quick-action title or email lookup). */
  crmContacts: ManagedContact[];
  crmContactsLoading: boolean;
  crmAccounts: Account[];
  /** True while GET /api/v1/crm/accounts list is in flight (Home Accounts KPI should skeleton, not 0). */
  crmAccountsLoading: boolean;

  emailComposeOpen: boolean;
  emailComposeContactId: string | null;
  emailComposePrefill: { subject?: string; body?: string } | null;
  openEmailComposeModal: (contactId: string, prefill?: { subject?: string; body?: string }) => void;
  closeEmailComposeModal: () => void;

  /** Shared task list from GET /api/v1/crm/tasks with optimistic mutations via crmCreate or crmPatch or crmDelete. */
  crmTasks: Task[];
  addTask: (task: Omit<Task, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }) => void;
  patchTask: (id: string, patch: Partial<Task>) => void;
  removeTask: (id: string) => void;

  /** Shared mock-backed activity list (optimistic mutations until activities API). */
  crmActivities: Activity[];
  addActivity: (activity: Omit<Activity, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }) => Promise<void>;
  patchActivity: (id: string, patch: Partial<Activity>) => Promise<void>;
  removeActivity: (id: string) => void;

  /** Shared mock-backed opportunity list (optimistic mutations until deals API). */
  crmOpportunities: Opportunity[];
  addOpportunity: (opportunity: Opportunity) => void;
  patchOpportunity: (id: string, patch: Partial<Opportunity>) => void;

  /**
   * Opportunities visible for export — excludes deleted and draft-new sentinel (not narrowed by URL filters;
   * hub pages apply `useCrmFilters` locally).
   */
  opportunities: Opportunity[];

  activityModalOpen: boolean;
  activityModalPrefill: ActivityModalOpenPrefill | null;
  openActivityModal: (prefill?: ActivityModalOpenPrefill) => void;
  closeActivityModal: () => void;

  /** Pipeline generation wizard — replaces opening ActivityModal with _mode pipeline_generation. */
  pipelineGenerationModalOpen: boolean;
  openPipelineModal: () => void;
  closePipelineModal: () => void;

  /** Instruction 3 action aliases */
  openDrawer: (opportunityId: string) => void;
  closeDrawer: () => void;
  setKpis: (kpis: CrmKpis) => void;

  removeOpportunity: (id: string) => void;
  removeContact: (id: string) => void;
  removeAccount: (id: string) => void;

  /** Merge or clear per-contact demo patches (owner, tags) for Contacts hub bulk actions. */
  mergeContactListPatches: (patches: Record<string, { ownerId?: string; tags?: string[] } | null>) => void;

  /** Dispatches a refresh tick so the provider refetches opportunities, tasks, and activities (Supabase realtime). */
  requestDataRefresh: () => void;
  /** Awaits the same refetch bundle used after `requestDataRefresh` — for `useCrmKpis().refetch()` and manual reload. */
  refetchAllCrmLists: () => Promise<void>;

  /** CRM-scoped assistant state — delegated to **`ShellAssistantProvider`** when mounted (#92). */
  crmAssistant: CrmAssistantState;
  openCrmAssistant: (opts: {
    prefillContext: string;
    suggestedPrompts: string[];
    pageContext?: CrmAiAssistantPageContext | null;
  }) => void;
  closeCrmAssistant: () => void;

  commandPaletteOpen: boolean;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;

  /** Flowbite destructive confirmation — use from `createCrmActions` instead of `window.confirm`. */
  confirmDestructive: (opts: CrmDestructiveConfirmOptions) => Promise<boolean>;
}

export type CrmBootstrapValue = {
  opportunitiesLoading: boolean;
  opportunitiesError: string | null;
  tasksLoading: boolean;
  tasksError: string | null;
  /** Refetch tasks list only — for TaskBoard “Try again” after GET /api/v1/crm/tasks failure. */
  refetchTasks: () => Promise<void>;
  activitiesLoading: boolean;
  activitiesError: string | null;
};

const CrmBootstrapContext = createContext<CrmBootstrapValue>({
  opportunitiesLoading: false,
  opportunitiesError: null,
  tasksLoading: false,
  tasksError: null,
  refetchTasks: async () => {},
  activitiesLoading: false,
  activitiesError: null,
});

export function useCrmBootstrap(): CrmBootstrapValue {
  return useContext(CrmBootstrapContext);
}

export type CrmApiBridgeValue = {
  deals: ReturnType<typeof useCrmDeals>['mutate'];
  tasks: ReturnType<typeof useCrmTasks>['mutate'];
  activities: ReturnType<typeof useCrmActivities>['mutate'];
  contacts: ReturnType<typeof useCrmContacts>['mutate'];
  accounts: ReturnType<typeof useCrmAccounts>['mutate'];
};

const CrmApiBridgeContext = createContext<CrmApiBridgeValue | null>(null);

export function useCrmApiBridge(): CrmApiBridgeValue {
  const v = useContext(CrmApiBridgeContext);
  if (!v) {
    throw new Error('useCrmApiBridge must be used within CrmProvider');
  }
  return v;
}

export type CrmRollbackValue = {
  restoreOpportunity: (id: string) => void;
  restoreContact: (id: string) => void;
  restoreAccount: (id: string) => void;
  restoreActivity: (id: string) => void;
};

const CrmRollbackContext = createContext<CrmRollbackValue | null>(null);

export function useCrmRollback(): CrmRollbackValue {
  const v = useContext(CrmRollbackContext);
  if (!v) {
    throw new Error('useCrmRollback must be used within CrmProvider');
  }
  return v;
}

const CrmContext = createContext<CrmContextValue | null>(null);

export function CrmProvider({ children }: { children: ReactNode }) {
  const shellCtx = useContext(ShellAssistantReactContext);
  const { currentTenant } = useTenant();
  const { currency: tenantCurrency } = useTenantDefaultCurrency(currentTenant?.id);
  const tenantLocale = localeForCurrency(tenantCurrency);
  const dealsHook = useCrmDeals();
  const tasksHook = useCrmTasks();
  const activitiesHook = useCrmActivities();
  const contactsHook = useCrmContacts();
  const accountsHook = useCrmAccounts();

  const crmOpportunities = dealsHook.data;
  const crmTasks = tasksHook.data;
  const crmActivitiesAll = activitiesHook.data;
  const crmContacts = contactsHook.data;
  const crmAccounts = accountsHook.data;

  const [crm, dispatch] = useReducer(crmReducer, initialCrmState);

  const crmActivities = useMemo(
    () => crmActivitiesAll.filter((a) => !crm.deletedActivityIds.includes(a.id)),
    [crmActivitiesAll, crm.deletedActivityIds]
  );

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleModalPrefill, setScheduleModalPrefill] = useState<CrmScheduleModalPrefill | null>(null);
  const [scheduleModalMode, setScheduleModalMode] = useState<'task' | 'activity'>('task');
  const [scheduleModalEditingTask, setScheduleModalEditingTask] = useState<Task | null>(null);
  const [scheduleModalEditMode, setScheduleModalEditMode] = useState(false);

  const scheduleModalEditingTaskRef = useRef<Task | null>(null);
  useEffect(() => {
    scheduleModalEditingTaskRef.current = scheduleModalEditingTask;
  }, [scheduleModalEditingTask]);

  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activityModalPrefill, setActivityModalPrefill] = useState<ActivityModalOpenPrefill | null>(null);
  const [createContactPrefill, setCreateContactPrefill] = useState<Record<string, string> | null>(null);
  const [createAccountModalOpen, setCreateAccountModalOpen] = useState(false);
  const [createAccountPrefill, setCreateAccountPrefill] = useState<Record<string, string> | null>(null);

  const [pipelineGenerationModalOpen, setPipelineGenerationModalOpen] = useState(false);

  const [contactDrawerPortalAsideEl, setContactDrawerPortalAsideEl] = useState<HTMLElement | null>(null);
  const registerContactDrawerFlowbitePortalAside = useCallback((el: HTMLElement | null) => {
    setContactDrawerPortalAsideEl(el);
  }, []);

  /** Ref is updated synchronously in `registerContactDrawerOverlayHost` so call prep never portals to `body` for one frame while the drawer host mounts. */
  const contactDrawerOverlayHostRef = useRef<HTMLElement | null>(null);
  const [contactDrawerOverlayHostGen, setContactDrawerOverlayHostGen] = useState(0);
  const registerContactDrawerOverlayHost = useCallback((el: HTMLElement | null) => {
    if (contactDrawerOverlayHostRef.current === el) return;
    contactDrawerOverlayHostRef.current = el;
    setContactDrawerOverlayHostGen((g) => g + 1);
  }, []);

  const readContactDrawerOverlayHost = useCallback(() => contactDrawerOverlayHostRef.current, []);

  /** Embed slot removed (#92) — noop keeps slice APIs stable for older call sites. */
  const registerCrmAiAssistantEmbedSlot = useCallback((_el: HTMLElement | null) => {}, []);

  const [localAssistant, setLocalAssistant] = useState<CrmAssistantState>(initialCrmAssistantState);

  const openCrmAssistant = useCallback(
    (opts: {
      prefillContext: string;
      suggestedPrompts: string[];
      pageContext?: CrmAiAssistantPageContext | null;
    }) => {
      if (shellCtx) {
        shellCtx.openCrmAssistant(opts);
        return;
      }
      blurActiveElementBeforeCrmOverlay();
      setLocalAssistant((s) => ({
        open: true,
        prefillContext: opts.prefillContext,
        suggestedPrompts: opts.suggestedPrompts,
        prefillNonce: s.prefillNonce + 1,
        pageContext: opts.pageContext ?? null,
      }));
    },
    [shellCtx]
  );

  const closeCrmAssistant = useCallback(() => {
    if (shellCtx) {
      shellCtx.closeCrmAssistant();
      return;
    }
    setLocalAssistant((s) => ({ ...s, open: false }));
  }, [shellCtx]);

  const crmAssistant = useMemo(
    () => (shellCtx ? sessionToCrmAssistantShape(shellCtx.session) : localAssistant),
    [shellCtx, localAssistant]
  );

  const openCommandPalette = useCallback(() => {
    blurActiveElementBeforeCrmOverlay();
    dispatch({ type: 'OPEN_COMMAND_PALETTE' });
  }, []);

  const closeCommandPalette = useCallback(() => {
    dispatch({ type: 'CLOSE_COMMAND_PALETTE' });
  }, []);

  const [destructiveDialog, setDestructiveDialog] = useState<CrmDestructiveConfirmOptions | null>(null);
  const destructiveResolveRef = useRef<((ok: boolean) => void) | null>(null);

  const settleDestructiveDialog = useCallback((ok: boolean) => {
    const r = destructiveResolveRef.current;
    destructiveResolveRef.current = null;
    r?.(ok);
    setDestructiveDialog(null);
  }, []);

  const confirmDestructive = useCallback((opts: CrmDestructiveConfirmOptions): Promise<boolean> => {
    if (destructiveResolveRef.current) {
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      destructiveResolveRef.current = resolve;
      setDestructiveDialog(opts);
    });
  }, []);

  const handleDestructiveCancel = useCallback(() => settleDestructiveDialog(false), [settleDestructiveDialog]);
  const handleDestructiveConfirm = useCallback(() => settleDestructiveDialog(true), [settleDestructiveDialog]);

  const setActiveTab = useCallback((tab: CrmTab) => {
    dispatch({ type: 'SET_ACTIVE_TAB', tab });
  }, []);

  const openDrawer = useCallback((opportunityId: string) => {
    dispatch({ type: 'OPEN_DRAWER', opportunityId });
  }, []);

  const closeDrawer = useCallback(() => {
    dispatch({ type: 'CLOSE_DRAWER' });
  }, []);

  const openContactDrawer = useCallback((contactId: string, prefill?: Record<string, string>) => {
    setCreateContactPrefill(prefill ?? null);
    dispatch({ type: 'OPEN_CONTACT_DRAWER', contactId });
  }, []);

  const closeContactDrawer = useCallback(() => {
    setCreateContactPrefill(null);
    dispatch({ type: 'CLOSE_CONTACT_DRAWER' });
  }, []);

  const openAccountDrawer = useCallback((id: string) => {
    dispatch({ type: 'OPEN_ACCOUNT_DRAWER', accountId: id });
  }, []);

  const closeAccountDrawer = useCallback(() => {
    dispatch({ type: 'CLOSE_ACCOUNT_DRAWER' });
  }, []);

  const openCreateAccountModal = useCallback((prefill?: Record<string, string>) => {
    blurActiveElementBeforeCrmOverlay();
    setCreateAccountPrefill(prefill ?? null);
    setCreateAccountModalOpen(true);
  }, []);

  const closeCreateAccountModal = useCallback(() => {
    setCreateAccountModalOpen(false);
    setCreateAccountPrefill(null);
  }, []);

  const openOpportunityDrawer = openDrawer;
  const closeOpportunityDrawer = closeDrawer;

  const setKpis = useCallback((kpis: CrmKpis) => {
    dispatch({ type: 'SET_KPIS', kpis });
  }, []);

  const refetchAllCrmLists = useCallback(async () => {
    await Promise.all([
      dealsHook.refetch(),
      tasksHook.refetch(),
      activitiesHook.refetch(),
      contactsHook.refetch(),
      accountsHook.refetch(),
    ]);
  }, [
    dealsHook.refetch,
    tasksHook.refetch,
    activitiesHook.refetch,
    contactsHook.refetch,
    accountsHook.refetch,
  ]);

  const requestDataRefresh = useCallback(() => {
    dispatch({ type: 'REQUEST_DATA_REFRESH' });
  }, []);

  useEffect(() => {
    if (crm.dataRefreshNonce < 1) return;
    void refetchAllCrmLists();
  }, [crm.dataRefreshNonce, refetchAllCrmLists]);

  const removeOpportunity = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_OPPORTUNITY', id });
  }, []);

  const removeContact = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_CONTACT', id });
  }, []);

  const removeAccount = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_ACCOUNT', id });
  }, []);

  const mergeContactListPatches = useCallback(
    (patches: Record<string, { ownerId?: string; tags?: string[] } | null>) => {
      dispatch({ type: 'MERGE_CONTACT_LIST_PATCHES', patches });
    },
    []
  );

  const restoreOpportunity = useCallback((id: string) => {
    dispatch({ type: 'RESTORE_OPPORTUNITY', id });
  }, []);

  const restoreContact = useCallback((id: string) => {
    dispatch({ type: 'RESTORE_CONTACT', id });
  }, []);

  const restoreAccount = useCallback((id: string) => {
    dispatch({ type: 'RESTORE_ACCOUNT', id });
  }, []);

  const removeActivity = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_ACTIVITY', id });
  }, []);

  const restoreActivity = useCallback((id: string) => {
    dispatch({ type: 'RESTORE_ACTIVITY', id });
  }, []);

  const closeDrilldown = useCallback(() => {
    dispatch({ type: 'CLOSE_DRILLDOWN' });
  }, []);

  const openDrilldown = useCallback((arg1: DrilldownConfig | string, arg2?: Record<string, unknown>) => {
    const run = () => {
      blurActiveElementBeforeCrmOverlay();
      if (typeof arg1 === 'string' && arg2 !== undefined) {
        dispatch({ type: 'OPEN_DRILLDOWN', title: arg1, filterPreset: arg2 });
        return;
      }
      const cfg = arg1 as DrilldownConfig;
      dispatch({
        type: 'OPEN_DRILLDOWN',
        title: cfg.title,
        filterPreset: {
          ...cfg.filterPreset,
          [DRILL_ENTITY]: cfg.entityType,
          [DRILL_SUBTITLE]: cfg.subtitle ?? '',
        },
      });
    };
    setTimeout(run, 0);
  }, []) as CrmContextValue['openDrilldown'];

  const closeScheduleModal = useCallback(() => {
    setScheduleModalOpen(false);
    setScheduleModalPrefill(null);
    setScheduleModalMode('task');
    setScheduleModalEditingTask(null);
    setScheduleModalEditMode(false);
  }, []);

  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const [emailComposeContactId, setEmailComposeContactId] = useState<string | null>(null);
  const [emailComposePrefill, setEmailComposePrefill] = useState<{
    subject?: string;
    body?: string;
  } | null>(null);

  const openEmailComposeModal = useCallback(
    (contactId: string, prefill?: { subject?: string; body?: string }) => {
      setEmailComposeContactId(contactId);
      setEmailComposePrefill(prefill ?? null);
      setEmailComposeOpen(true);
    },
    [],
  );

  const closeEmailComposeModal = useCallback(() => {
    setEmailComposeOpen(false);
    setEmailComposeContactId(null);
    setEmailComposePrefill(null);
  }, []);

  const patchTask = useCallback(
    (id: string, patch: Partial<Task>) => {
      void tasksHook.mutate.patchTask(id, patch);
    },
    [tasksHook.mutate]
  );

  const addTask = useCallback(
    (task: Omit<Task, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }) => {
      void tasksHook.mutate.addTask(task);
    },
    [tasksHook.mutate]
  );

  const removeTask = useCallback(
    (id: string) => {
      if (scheduleModalEditingTaskRef.current?.id === id) {
        closeScheduleModal();
      }
      void tasksHook.mutate.removeTask(id);
    },
    [closeScheduleModal, tasksHook.mutate]
  );

  const patchActivity = useCallback(
    async (id: string, patch: Partial<Activity>) => {
      await activitiesHook.mutate.patchActivity(id, patch);
    },
    [activitiesHook.mutate]
  );

  const addActivity = useCallback(
    async (activity: Omit<Activity, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }) => {
      await activitiesHook.mutate.addActivity(activity);
    },
    [activitiesHook.mutate]
  );

  const patchOpportunity = dealsHook.mutate.patchOpportunity;

  const addOpportunity = dealsHook.mutate.addOpportunity;

  const openScheduleModal = useCallback(
    (
      prefill?: CrmScheduleModalPrefill,
      options?: { mode?: 'task' | 'activity'; editingTask?: Task; editMode?: boolean }
    ) => {
      blurActiveElementBeforeCrmOverlay();
      let editing: Task | null = options?.editingTask ?? null;
      if (!editing && options?.editMode && prefill && typeof (prefill as Partial<Task>).id === 'string') {
        const tid = (prefill as Partial<Task>).id!;
        editing = crmTasks.find((t) => t.id === tid) ?? null;
      }
      const isUpdateFlow = Boolean(options?.editingTask || (options?.editMode && editing));
      setScheduleModalEditMode(isUpdateFlow);
      setScheduleModalEditingTask(editing);
      setScheduleModalPrefill(editing ? null : (prefill ?? null));
      setScheduleModalMode(options?.mode ?? 'task');
      setScheduleModalOpen(true);
    },
    [crmTasks]
  );

  const openActivityModal = useCallback((prefill?: ActivityModalOpenPrefill) => {
    blurActiveElementBeforeCrmOverlay();
    if (!prefill) {
      setActivityModalPrefill(null);
      setActivityModalOpen(true);
      return;
    }
    const copy: ActivityModalOpenPrefill = { ...prefill };
    delete copy.persistWithoutModal;
    setActivityModalPrefill(Object.keys(copy).length ? copy : null);
    setActivityModalOpen(true);
  }, []);

  const closeActivityModal = useCallback(() => {
    setActivityModalOpen(false);
    setActivityModalPrefill(null);
  }, []);

  const openPipelineModal = useCallback(() => {
    blurActiveElementBeforeCrmOverlay();
    setPipelineGenerationModalOpen(true);
  }, []);

  const closePipelineModal = useCallback(() => {
    setPipelineGenerationModalOpen(false);
  }, []);

  const activeDrilldown = useMemo(
    () => (crm.openDrilldown ? drilldownConfigFromShell(crm.openDrilldown) : null),
    [crm.openDrilldown]
  );

  const drilldownOpen = crm.openDrilldown !== null;

  const opportunityDrawerOpen = crm.openDrawerId !== null;
  const activeOpportunityId = crm.openDrawerId;

  const contactDrawerOpen = crm.activeContactId !== null;
  const activeContactId = crm.activeContactId;

  const accountDrawerOpen = crm.activeAccountId !== null;
  const activeAccountId = crm.activeAccountId;

  const opportunities = useMemo(() => {
    return crmOpportunities.filter(
      (o) => !crm.deletedOpportunityIds.includes(o.id) && o.id !== CRM_NEW_OPPORTUNITY_SENTINEL
    );
  }, [crmOpportunities, crm.deletedOpportunityIds]);

  const value = useMemo(
    () =>
      ({
        crm,
        tenantCurrency,
        tenantLocale,
        activeTab: crm.activeTab as CrmTab,
        setActiveTab,
        opportunityDrawerOpen,
        openOpportunityDrawer,
        closeOpportunityDrawer,
        activeOpportunityId,
        contactDrawerOpen,
        activeContactId,
        createContactPrefill,
        openContactDrawer,
        closeContactDrawer,
        contactDrawerPortalAsideEl,
        registerContactDrawerFlowbitePortalAside,
        contactDrawerOverlayHostEl: contactDrawerOverlayHostRef.current,
        registerContactDrawerOverlayHost,
        readContactDrawerOverlayHost,
        contactDrawerOverlayHostGen,
        crmAiAssistantEmbedSlotEl: null,
        registerCrmAiAssistantEmbedSlot,
        accountDrawerOpen,
        activeAccountId,
        openAccountDrawer,
        closeAccountDrawer,
        createAccountModalOpen,
        createAccountPrefill,
        openCreateAccountModal,
        closeCreateAccountModal,
        drilldownOpen,
        openDrilldown,
        closeDrilldown,
        activeDrilldown,
        scheduleModalOpen,
        scheduleModalPrefill,
        scheduleModalMode,
        scheduleModalEditingTask,
        scheduleModalEditMode,
        openScheduleModal,
        closeScheduleModal,
        crmContacts,
        crmContactsLoading: contactsHook.isLoading,
        crmAccounts,
        crmAccountsLoading: accountsHook.isLoading,
        emailComposeOpen,
        emailComposeContactId,
        emailComposePrefill,
        openEmailComposeModal,
        closeEmailComposeModal,
        crmTasks,
        addTask,
        patchTask,
        removeTask,
        crmActivities,
        addActivity,
        patchActivity,
        removeActivity,
        crmOpportunities,
        addOpportunity,
        patchOpportunity,
        opportunities,
        activityModalOpen,
        activityModalPrefill,
        openActivityModal,
        closeActivityModal,
        pipelineGenerationModalOpen,
        openPipelineModal,
        closePipelineModal,
        openDrawer,
        closeDrawer,
        setKpis,
        removeOpportunity,
        removeContact,
        removeAccount,
        mergeContactListPatches,
        requestDataRefresh,
        refetchAllCrmLists,
        crmAssistant,
        openCrmAssistant,
        closeCrmAssistant,
        commandPaletteOpen: crm.commandPaletteOpen,
        openCommandPalette,
        closeCommandPalette,
        confirmDestructive,
      }) satisfies CrmContextValue,
    [
      crm,
      opportunityDrawerOpen,
      activeOpportunityId,
      contactDrawerOpen,
      activeContactId,
      createContactPrefill,
      accountDrawerOpen,
      activeAccountId,
      createAccountModalOpen,
      createAccountPrefill,
      drilldownOpen,
      activeDrilldown,
      scheduleModalOpen,
      scheduleModalPrefill,
      scheduleModalMode,
      scheduleModalEditingTask,
      scheduleModalEditMode,
      crmTasks,
      activityModalOpen,
      activityModalPrefill,
      pipelineGenerationModalOpen,
      setActiveTab,
      openOpportunityDrawer,
      closeOpportunityDrawer,
      openContactDrawer,
      closeContactDrawer,
      contactDrawerPortalAsideEl,
      registerContactDrawerFlowbitePortalAside,
      contactDrawerOverlayHostGen,
      registerContactDrawerOverlayHost,
      readContactDrawerOverlayHost,
      registerCrmAiAssistantEmbedSlot,
      openAccountDrawer,
      closeAccountDrawer,
      openCreateAccountModal,
      closeCreateAccountModal,
      openDrilldown,
      closeDrilldown,
      openScheduleModal,
      closeScheduleModal,
      crmContacts,
      contactsHook.isLoading,
      crmAccounts,
      accountsHook.isLoading,
      emailComposeOpen,
      emailComposeContactId,
      emailComposePrefill,
      openEmailComposeModal,
      closeEmailComposeModal,
      addTask,
      patchTask,
      removeTask,
      crmActivities,
      addActivity,
      patchActivity,
      removeActivity,
      crmOpportunities,
      addOpportunity,
      patchOpportunity,
      opportunities,
      openActivityModal,
      closeActivityModal,
      openPipelineModal,
      closePipelineModal,
      openDrawer,
      closeDrawer,
      setKpis,
      removeOpportunity,
      removeContact,
      removeAccount,
      mergeContactListPatches,
      requestDataRefresh,
      refetchAllCrmLists,
      crmAssistant,
      openCrmAssistant,
      closeCrmAssistant,
      openCommandPalette,
      closeCommandPalette,
      confirmDestructive,
      tenantCurrency,
      tenantLocale,
    ]
  );

  const bootstrap = useMemo(
    () => ({
      opportunitiesLoading: dealsHook.isLoading,
      opportunitiesError: dealsHook.error,
      tasksLoading: tasksHook.isLoading,
      tasksError: tasksHook.error,
      refetchTasks: tasksHook.refetch,
      activitiesLoading: activitiesHook.isLoading,
      activitiesError: activitiesHook.error,
    }),
    [
      dealsHook.isLoading,
      dealsHook.error,
      tasksHook.isLoading,
      tasksHook.error,
      tasksHook.refetch,
      activitiesHook.isLoading,
      activitiesHook.error,
    ]
  );

  const apiBridge = useMemo(
    () => ({
      deals: dealsHook.mutate,
      tasks: tasksHook.mutate,
      activities: activitiesHook.mutate,
      contacts: contactsHook.mutate,
      accounts: accountsHook.mutate,
    }),
    [dealsHook.mutate, tasksHook.mutate, activitiesHook.mutate, contactsHook.mutate, accountsHook.mutate]
  );

  const rollback = useMemo(
    () => ({
      restoreOpportunity,
      restoreContact,
      restoreAccount,
      restoreActivity,
    }),
    [restoreOpportunity, restoreContact, restoreAccount, restoreActivity]
  );

  return (
    <CrmBootstrapContext.Provider value={bootstrap}>
      <CrmApiBridgeContext.Provider value={apiBridge}>
        <CrmRollbackContext.Provider value={rollback}>
          <CrmContext.Provider value={value}>
            {children}
            <DestructiveConfirmDialog
              open={destructiveDialog !== null}
              title={destructiveDialog?.title ?? ''}
              message={destructiveDialog?.message ?? ''}
              confirmLabel={destructiveDialog?.confirmLabel ?? 'Confirm'}
              onCancel={handleDestructiveCancel}
              onConfirm={handleDestructiveConfirm}
            />
          </CrmContext.Provider>
        </CrmRollbackContext.Provider>
      </CrmApiBridgeContext.Provider>
    </CrmBootstrapContext.Provider>
  );
}

export function useCrm(): CrmContextValue {
  const ctx = useContext(CrmContext);
  if (!ctx) {
    throw new Error('useCrm must be used within CrmProvider');
  }
  return ctx;
}
